import { GamesIndexProps } from "@/types/types"
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react"
import { Bars3BottomLeftIcon } from "@heroicons/react/24/outline"
import { router, usePage } from "@inertiajs/react"
import clsx from "clsx"

const sortingOptions = {
  games: [
    {
      label: "Date Added (Newest First)",
      sortBy: "created_at",
      sortDirection: "desc",
    },
    {
      label: "Date Added (Oldest First)",
      sortBy: "created_at",
      sortDirection: "asc",
    },
    {
      label: "Date Played (Newest First)",
      sortBy: "date",
      sortDirection: "desc",
    },
    {
      label: "Date Played (Oldest First)",
      sortBy: "date",
      sortDirection: "asc",
    },
  ],
  events: [
    {
      label: "Date Added (Newest First)",
      sortBy: "created_at",
      sortDirection: "desc",
    },
    {
      label: "Date Added (Oldest First)",
      sortBy: "created_at",
      sortDirection: "asc",
    },
    {
      label: "Name (A to Z)",
      sortBy: "name",
      sortDirection: "asc",
    },
    {
      label: "Name (Z to A)",
      sortBy: "name",
      sortDirection: "desc",
    },
  ],
  players: [
    {
      label: "Date Added (Newest First)",
      sortBy: "created_at",
      sortDirection: "desc",
    },
    {
      label: "Date Added (Oldest First)",
      sortBy: "created_at",
      sortDirection: "asc",
    },
    {
      label: "Name (A to Z)",
      sortBy: "name",
      sortDirection: "asc",
    },
    {
      label: "Name (Z to A)",
      sortBy: "name",
      sortDirection: "desc",
    },
  ],
  puzzles: [
    {
      label: "Date Added (Newest First)",
      sortBy: "created_at",
      sortDirection: "desc",
    },
    {
      label: "Date Added (Oldest First)",
      sortBy: "created_at",
      sortDirection: "asc",
    },
    {
      label: "Title (A to Z)",
      sortBy: "title",
      sortDirection: "asc",
    },
    {
      label: "Title (Z to A)",
      sortBy: "title",
      sortDirection: "desc",
    },
  ],
}

const SortingSelector = ({ type }: { type: keyof typeof sortingOptions }) => {
  const { filters } = usePage<GamesIndexProps>().props
  const options = sortingOptions[type]

  const selectedSortingOption =
    options.find(
      (option) =>
        option.sortBy === filters.sort_by &&
        option.sortDirection === filters.sort_direction
    ) || options[0]

  const handleSortingChange = (option: (typeof options)[number]) => {
    const query = new URLSearchParams(window.location.search)

    if (
      option.sortBy === filters.sort_by &&
      option.sortDirection === filters.sort_direction
    ) {
      return
    }

    if (
      option.sortBy === options[0].sortBy &&
      option.sortDirection === options[0].sortDirection
    ) {
      query.delete("sort_by")
      query.delete("sort_direction")
    } else {
      query.set("sort_by", option.sortBy)
      query.set("sort_direction", option.sortDirection)
    }

    router.get(window.location.pathname, Object.fromEntries(query.entries()), {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  return (
    <Listbox
      value={selectedSortingOption}
      by="label"
      onChange={handleSortingChange}
    >
      <Label className="sr-only">Select sorting option</Label>
      <div className="relative w-full max-w-64">
        <div className="relative flex h-10 rounded-xl bg-white shadow-xs">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Bars3BottomLeftIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <ListboxButton className="focus:ring-chessdrop-600 block h-full w-full rounded-xl border-0 py-2 pr-5 pl-10 text-left text-sm text-gray-700 ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:outline-none focus:ring-inset">
            {selectedSortingOption.label}
          </ListboxButton>
        </div>
        <ListboxOptions className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded-xl bg-white py-1.5 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
          {options.map((option) => (
            <ListboxOption
              key={option.label}
              value={option}
              className={clsx([
                "relative cursor-pointer px-3 py-2 text-sm text-gray-700 select-none",
                "data-focus:bg-gray-100 data-focus:text-gray-700",
                "data-selected:bg-chessdrop-600 data-selected:text-white",
                "data-selected:data-focus:bg-chessdrop-600 data-selected:data-focus:text-white",
              ])}
            >
              {option.label}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  )
}

export default SortingSelector
